import { apiClient } from "../../lib/http/apiClient";
import type { OrgAdminLoginResponse, OrgAdminMe, OrgCandidateUser, OrganizationSummary } from "./types";

export { orgAdminStorage } from "../../lib/storage/orgAdminStorage";
import { orgAdminStorage } from "../../lib/storage/orgAdminStorage";

/** Always the ORG-ADMIN bearer token — never `adminStorage`'s superadmin one.
 *  (apiClient's request interceptor only injects the regular user token when no
 *  Authorization header is already set, so every call below is unambiguous.) */
export function orgAdminHeaders() {
  const token = orgAdminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const orgAdminService = {
  async login(email: string, password: string): Promise<OrgAdminLoginResponse> {
    const { data } = await apiClient.post<OrgAdminLoginResponse>("/api/org-admin/login", { email, password });
    orgAdminStorage.setSession(data.access_token, data.user);
    return data;
  },

  /** Redeems a superadmin-issued one-off link (?token=...) — public endpoint,
   *  the token itself is the credential. Same response shape as `login`. */
  async magicLogin(token: string): Promise<OrgAdminLoginResponse> {
    const { data } = await apiClient.post<OrgAdminLoginResponse>("/api/org-admin/magic-login", { token });
    orgAdminStorage.setSession(data.access_token, data.user);
    return data;
  },

  /** Clears ONLY the org-admin session — unlike `adminService.logout()`, which
   *  wipes all of localStorage and would take a superadmin or buyer/seller
   *  session in the same browser down with it. */
  logout() {
    orgAdminStorage.clearSession();
  },

  async getMe(): Promise<OrgAdminMe> {
    const { data } = await apiClient.get<OrgAdminMe>("/api/org-admin/me", { headers: orgAdminHeaders() });
    return data;
  },

  async getOrganization(): Promise<OrganizationSummary> {
    const { data } = await apiClient.get<OrganizationSummary>("/api/org-admin/organization", {
      headers: orgAdminHeaders(),
    });
    return data;
  },

  /** The pool superadmin curated for this org — an org admin can never reach
   *  outside it. */
  async getCandidates(): Promise<OrgCandidateUser[]> {
    const { data } = await apiClient.get<OrgCandidateUser[]>("/api/org-admin/candidates", {
      headers: orgAdminHeaders(),
    });
    return data;
  },

  async getInvited(): Promise<OrgCandidateUser[]> {
    const { data } = await apiClient.get<OrgCandidateUser[]>("/api/org-admin/invited", {
      headers: orgAdminHeaders(),
    });
    return data;
  },

  async getMembers(): Promise<OrgCandidateUser[]> {
    const { data } = await apiClient.get<OrgCandidateUser[]>("/api/org-admin/members", {
      headers: orgAdminHeaders(),
    });
    return data;
  },

  async inviteCandidate(userId: string): Promise<{ user_id: string; status: string }> {
    const { data } = await apiClient.post<{ user_id: string; status: string }>(
      `/api/org-admin/candidates/${encodeURIComponent(userId)}/invite`,
      {},
      { headers: orgAdminHeaders() },
    );
    return data;
  },

  async uninviteCandidate(userId: string): Promise<{ user_id: string; status: string }> {
    const { data } = await apiClient.post<{ user_id: string; status: string }>(
      `/api/org-admin/candidates/${encodeURIComponent(userId)}/uninvite`,
      {},
      { headers: orgAdminHeaders() },
    );
    return data;
  },

  async removeMember(userId: string): Promise<{ user_id: string; removed: boolean }> {
    const { data } = await apiClient.post<{ user_id: string; removed: boolean }>(
      `/api/org-admin/members/${encodeURIComponent(userId)}/remove`,
      {},
      { headers: orgAdminHeaders() },
    );
    return data;
  },
};
